import { forwardRef } from 'react';
import { Box, Typography } from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
} from 'recharts';
import LayersIcon from '@mui/icons-material/Layers';
import AppsIcon from '@mui/icons-material/Apps';
import DescriptionIcon from '@mui/icons-material/Description';
import PeopleIcon from '@mui/icons-material/People';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import type { UsageReportData } from '../types';

// Use local logo to avoid CORS issues with html2canvas
const PELLES_LOGO_LOCAL = '/pelles-logo.png';

const PellesLogo = () => (
  <Box
    component="img"
    src={PELLES_LOGO_LOCAL}
    alt="Pelles.AI"
    sx={{
      height: 40,
      width: 'auto',
    }}
  />
);

interface MetricCardProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  color?: string;
  iconColor?: string;
}

const MetricCard = ({ icon, label, value, color = '#60a5fa', iconColor = '#93c5fd' }: MetricCardProps) => (
  <Box
    sx={{
      position: 'relative',
      bgcolor: 'white',
      borderRadius: '12px 12px 4px 12px',
      p: 2,
      pt: 2.5,
      minWidth: 180,
      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 0,
    }}
  >
    {/* Icon positioned at top-left, overlapping the card */}
    <Box
      sx={{
        position: 'absolute',
        top: -14,
        left: -8,
        color: iconColor,
        bgcolor: 'white',
        borderRadius: '50%',
        width: 36,
        height: 36,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      }}
    >
      {icon}
    </Box>
    <Typography
      sx={{
        fontSize: 13,
        color: '#64748b',
        textAlign: 'center',
        lineHeight: 1.3,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: 28,
        fontWeight: 600,
        color: color,
        mt: -0.5,
      }}
    >
      {typeof value === 'number' ? value.toLocaleString() : value}
    </Typography>
  </Box>
);

interface UsageReportProps {
  data: UsageReportData;
}

const UsageReport = forwardRef<HTMLDivElement, UsageReportProps>(({ data }, ref) => {
  // Prepare chart data - take last 30 days if more
  const chartData = data.dailyLogins.slice(-30).map((d) => ({
    ...d,
    shortLabel: d.date.slice(5), // MM-DD format
  }));

  // Get top 3 users
  const topUsers = data.topUsers.slice(0, 3);

  return (
    <Box
      ref={ref}
      sx={{
        width: 1200,
        minHeight: 800,
        background: 'linear-gradient(135deg, #f8fafc 0%, #e0f2fe 50%, #fce7f3 100%)',
        position: 'relative',
        overflow: 'hidden',
        fontFamily: '"Lato", "Helvetica Neue", Arial, sans-serif',
      }}
    >
      {/* Decorative wave at bottom */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: 150,
          background: 'linear-gradient(180deg, transparent 0%, rgba(59, 130, 246, 0.1) 100%)',
          borderTopLeftRadius: '50% 100%',
          borderTopRightRadius: '50% 100%',
        }}
      />

      {/* Content */}
      <Box sx={{ position: 'relative', p: 4 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          <PellesLogo />
          <Typography sx={{ color: '#64748b', fontSize: 14 }}>
            {data.reportDate}
          </Typography>
        </Box>

        {/* Title */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography
            sx={{
              fontSize: 32,
              fontWeight: 700,
              color: '#475569',
              mb: 0.5,
            }}
          >
            Pelles.ai Usage Report - <Box component="span" sx={{ textTransform: 'capitalize' }}>{data.orgName}</Box>
          </Typography>
          <Typography sx={{ color: '#3b82f6', fontSize: 16 }}>
            Last {data.periodDays} days
          </Typography>
        </Box>

        {/* Top Metrics Row */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 3,
            mb: 4,
            mt: 2,
            flexWrap: 'wrap',
          }}
        >
          <MetricCard
            icon={<LayersIcon sx={{ fontSize: 22 }} />}
            label="New projects"
            value={data.newProjects}
            color="#60a5fa"
            iconColor="#93c5fd"
          />
          <MetricCard
            icon={<AppsIcon sx={{ fontSize: 22 }} />}
            label="Active projects"
            value={data.activeProjects}
            color="#60a5fa"
            iconColor="#93c5fd"
          />
          <MetricCard
            icon={<DescriptionIcon sx={{ fontSize: 22 }} />}
            label="Total events"
            value={data.totalEvents}
            color="#22d3ee"
            iconColor="#67e8f9"
          />
          <MetricCard
            icon={<PeopleIcon sx={{ fontSize: 22 }} />}
            label="Active users"
            value={data.activeUsers}
            color="#4ade80"
            iconColor="#86efac"
          />
        </Box>

        {/* Bottom Metrics Row */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 3,
            mb: 4,
            flexWrap: 'wrap',
          }}
        >
          <MetricCard
            icon={<HelpOutlineIcon sx={{ fontSize: 22 }} />}
            label="DoubleCheck queries"
            value={data.totalQueries}
            color="#a78bfa"
            iconColor="#c4b5fd"
          />
          <MetricCard
            icon={<AutoAwesomeIcon sx={{ fontSize: 22 }} />}
            label="AI-generated reports"
            value={data.totalWorkflows}
            color="#a78bfa"
            iconColor="#c4b5fd"
          />
          <MetricCard
            icon={<AccessTimeIcon sx={{ fontSize: 22 }} />}
            label="Total time saved"
            value={`${data.timeSavedHours} hours`}
            color="#fbbf24"
            iconColor="#fcd34d"
          />
        </Box>

        {/* Chart Section */}
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
          {/* Most Active Users Card */}
          <Box
            sx={{
              position: 'relative',
              bgcolor: 'white',
              borderRadius: '12px 12px 4px 12px',
              p: 2.5,
              pt: 3,
              minWidth: 200,
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            }}
          >
            {/* Crown icon positioned at top-left */}
            <Box
              sx={{
                position: 'absolute',
                top: -14,
                left: -8,
                color: '#fbbf24',
                bgcolor: 'white',
                borderRadius: '50%',
                width: 36,
                height: 36,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              }}
            >
              <EmojiEventsIcon sx={{ fontSize: 24 }} />
            </Box>
            <Typography sx={{ color: '#22d3ee', fontWeight: 600, fontSize: 14, mb: 1.5, mt: 1 }}>
              Most active users
            </Typography>
            {topUsers.map((user, idx) => (
              <Typography
                key={user.userId}
                sx={{
                  fontSize: 14,
                  fontWeight: idx === 0 ? 600 : 400,
                  color: '#1e293b',
                  mb: 0.75,
                }}
              >
                {user.userName}
              </Typography>
            ))}
          </Box>

          {/* Chart */}
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{
                textAlign: 'center',
                color: '#22d3ee',
                fontWeight: 600,
                mb: 1,
                fontSize: 14,
              }}
            >
              Unique logins
            </Typography>
            <Box sx={{ width: '100%', height: 280, bgcolor: 'white', borderRadius: '12px 12px 4px 12px', p: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 10, right: 20, bottom: 45, left: 5 }}>
                  <defs>
                    <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#22d3ee" stopOpacity={1} />
                      <stop offset="100%" stopColor="#60a5fa" stopOpacity={0.8} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis
                    dataKey="shortLabel"
                    stroke="#94a3b8"
                    tick={{ fontSize: 9, fontFamily: 'Lato, sans-serif' }}
                    angle={-45}
                    textAnchor="end"
                    height={50}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    stroke="#94a3b8"
                    tick={{ fontSize: 10, fontFamily: 'Lato, sans-serif' }}
                    axisLine={false}
                    tickLine={false}
                    width={25}
                  />
                  <Bar dataKey="uniqueUsers" fill="url(#barGradient)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Box>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 20,
            right: 30,
            bgcolor: '#3b82f6',
            color: 'white',
            px: 3,
            py: 1,
            borderRadius: 2,
            fontSize: 14,
            fontWeight: 500,
          }}
        >
          www.pelles.ai
        </Box>
      </Box>
    </Box>
  );
});

UsageReport.displayName = 'UsageReport';

export default UsageReport;
